'use client'

import { isNull } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { Fragment } from 'react'

import { Slot } from '@/shared/lib'
import { Chip, MobileLayout, Separator } from '@/shared/ui'

import { useCategory } from '../../../lib/category'
import { useArticlesInfinite } from '../../../model/articles'
import { MobileCampaign } from '../../list'

type Props = {
  pageType: 'host' | 'guest'
  categoryId: number | null
}

export const MobileArticles = ({ pageType, categoryId }: Props) => {
  const t = useTranslations('magazine')
  const { handleCategoryChange, selectedCategory } = useCategory()

  const { articles, articleCategories, ref } = useArticlesInfinite({
    pageType,
    categoryId,
  })

  return (
    <MobileLayout className="flex flex-col pt-8 pb-0">
      <ul className="scrollbar-hide -mr-4 mb-6 flex max-w-[calc(100vw-16px)] gap-1.5 overflow-x-auto">
        <li>
          <Chip
            variant="solid"
            selected={isNull(selectedCategory)}
            onClick={handleCategoryChange(null, true)}
          >
            {t('tab.all')}
          </Chip>
        </li>
        {articleCategories?.map(({ id, name }) => (
          <li key={id}>
            <Chip
              variant="solid"
              selected={selectedCategory === id}
              onClick={handleCategoryChange(id, true)}
            >
              {name}
            </Chip>
          </li>
        ))}
      </ul>

      <div className="grow">
        <ul className="flex flex-col">
          {articles.map(
            (
              { id, title, description, landingPageCode, image, categoryName },
              index,
            ) => {
              const notLastItem = index !== articles.length - 1

              return (
                <Fragment key={id}>
                  <MobileCampaign.Item
                    href={`/${pageType}/campaign/article/${landingPageCode}`}
                    orientation="horizontal"
                  >
                    <Slot name="image">{image}</Slot>
                    <Slot name="category">{categoryName}</Slot>
                    <Slot name="title">{title}</Slot>
                    <Slot name="subContent">{description}</Slot>
                  </MobileCampaign.Item>

                  {notLastItem && (
                    <li
                      key={`separator-${index}`}
                      role="presentation"
                      aria-hidden="true"
                      className="my-4"
                    >
                      <Separator color="light-gray" />
                    </li>
                  )}
                </Fragment>
              )
            },
          )}
        </ul>
      </div>
      <div ref={ref} className="relative py-5" aria-hidden="true" />
    </MobileLayout>
  )
}
